/**
 * Rectangular Geogrid Ttot Calculation Service
 * Implements rectangular geometry calculations using Excel formulas from temp_Geogrid_Rect_cop_output/GEOGRID DESIGN.txt
 * Key differences from circular: Area = 4*B*L instead of π*r², stress spreading based on deck dimensions
 *
 * ✅ DYNAMIC IMPLEMENTATION - NO HARDCODED VALUES:
 * - H values: Use input arrays H_base[] and H_subbase[] (Excel B81-G81, B105-G105)
 * - Design strain εr: Dynamic per layer from designStrainBase[] and designStrainSubbase[] arrays (Excel B99-G99, B123-G123)
 * - Z_next calculations: Use exact Excel formulas with H of next layer (C82: $B$66-C81, C106: $B$72-C105)
 * - Membrane coefficient Ω: Varies per layer based on each layer's εr using VLOOKUP table
 * - All calculations follow Excel formulas exactly without approximations
 */

import { logDebug } from '../utils/utils';
import { LogTag } from '../config/loggingConfig';
import { MembraneCoefficientService } from './MembraneCoefficientService';
import { resolveGeogridSafetyFactor } from './GeogridSafetyFactor';

/**
 * GEOGRID DESIGN sheet the rectangular calculation follows. The two workbooks differ only in the base course
 * (B83 Tz guard, B94/B95 first-lift membrane): containerYard = MacContainer_Geogrid_Rect_v2 ('Rectangular' geometry),
 * railway = 2_MacRailway_GeoGrid_Rect_v_1.0 ('Railway' geometry).
 */
export type RectangularGeogridWorkbook = 'containerYard' | 'railway';

// Input interface for rectangular geogrid calculations
export interface RectangularGeogridInputs {
    workbook: RectangularGeogridWorkbook;

    // LOAD SECTION (Excel C8-C11)
    B: number; // Deck half width (m) - C8
    L: number; // Deck half length (m) - C9
    q: number; // Uniform load (kPa) - C10
    A0: number; // Contact area (m²) - C11 = B*L*4

    // TRUCK LOAD FOR FIRST LIFT SECTION (Excel C14-C16)
    r0: number; // Radius of wheel (m) - C14
    pw: number; // Tire pressure (kPa) - C15
    h_star: number; // Thickness h* (m) - C16

    // TOP COURSE SECTION (Excel C19-C21)
    γ1: number; // Unit weight (kN/m³) - C19
    h1: number; // Thickness (m) - C20
    α1: number; // Load spreading angle (deg) - C21

    // BASE COURSE SECTION (Excel C24-C31)
    γ2: number; // Unit weight (kN/m³) - C24
    φ2: number; // Friction angle (deg) - C25
    c2: number; // Cohesion (kPa) - C26
    h2: number; // Base course thickness (m) - C27
    α2: number; // Load spreading angle (deg) - C28
    Nr2: number; // Number of layers - C29
    Sv2: number; // Average geogrid spacing (m) - C30
    K2: number; // Active pressure coefficient - C31

    // SUBBASE COURSE SECTION (Excel C34-C41)
    γ3: number; // Unit weight (kN/m³) - C34
    φ3: number; // Friction angle (deg) - C35
    c3: number; // Cohesion (kPa) - C36
    h3: number; // Subbase course thickness (m) - C37
    α3: number; // Load spreading angle (deg) - C38
    Nr3: number; // Number of layers - C39
    Sv3: number; // Average geogrid spacing (m) - C40
    K3: number; // Active pressure coefficient - C41

    // SUBGRADE SECTION (Excel C44-C45)
    CBR: number; // CBR - C44
    FS: number; // FS BEARING CAPACITY - C45

    // LAYER SPECIFIC ARRAYS (Excel B81-G81, B105-G105, B99-G99, B123-G123)
    H_base: number[]; // H values for base course layers (Excel B81-G81) - [0.1, 0, 0, 0, 0, 0]
    H_subbase: number[]; // H values for subbase layers (Excel B105-G105) - [0.1, 0.4, 0, 0, 0, 0]
    designStrainBase: number[]; // Design strain % for base layers (Excel B99-G99) - [5, 2, 3, 4, 5, 2]
    designStrainSubbase: number[]; // Design strain % for subbase layers (Excel B123-G123) - [5, 1, 3, 4, 5, 2]
}

// Interface values at layer interfaces (Excel B60-B76)
export interface RectangularLayerInterfaceValues {
    asphaltLayerBottom: {
        z1: number; // depth (m) - B60
        B1: number; // half width at this depth - B61
        L1: number; // half length at this depth - B62
        A1: number; // area (m²) - B63
        σv1: number; // vertical stress (kPa) - B64
    };
    baseCourseBottom: {
        z2: number; // depth (m) - B66
        B2: number; // half width at this depth - B67
        L2: number; // half length at this depth - B68
        A2: number; // area (m²) - B69
        σv2: number; // vertical stress (kPa) - B70
    };
    subbaseBottom: {
        z3: number; // depth (m) - B72
        B3: number; // half width at this depth - B73
        L3: number; // half length at this depth - B74
        A3: number; // area (m²) - B75
        σv3: number; // vertical stress (kPa) - B76
    };
}

// Layer calculation for base/subbase courses
export interface RectangularLayerCalculation {
    layerNumber: number; // LAYER NUMBER (Excel B80/B104)
    H: number; // H (m) - from base course bottom (Excel B81/B105)
    Z: number; // Z (m) - from top (Excel B82/B106)
    Tz: number; // Tz (kN/m) - Earth pressure component (Excel B83/B107)
    A: number; // A (m²) - Area at this layer (Excel B84/B108)
    σv: number; // σv (kPa) - Vertical stress (Excel B85/B109)
    Tp: number; // Tp (kN/m) - Geogrid tension component (Excel B86/B110)
    εr: number; // εr (%) - Design strain (Excel B87/B111)
    Ω: number; // Ω - Membrane theory coefficient (Excel B88/B112)
    Ttot: number; // Ttot (kN/m) - Total tension (Excel B96/B120)
    // First lift calculations (only for layer 1)
    firstLift?: {
        r0: number; // radius of wheel (m) - Excel B89/B113
        h_star: number; // thickness h* (m) - Excel B90/B114
        rf: number; // rf (m) - Excel B91/B115
        pw: number; // tyre pressure (kPa) - Excel B92/B116
        Af: number; // Af (m) - Excel B93/B117
        Wtc: number; // Wtc (kPa) - Excel B94/B118
        Tm: number; // Tm (kN/m) - Excel B95/B119
    };
}

// Complete geogrid design table result
export interface RectangularGeogridDesignTable {
    interfaceValues: RectangularLayerInterfaceValues;
    baseCourse: RectangularLayerCalculation[];
    subbaseCourse: RectangularLayerCalculation[];
}

export interface RectangularGeogridResult {
    value: number; // Ttot value for requested layer
    column: string; // Excel column reference
    status: 'success' | 'error';
    message: string;
    designTable?: RectangularGeogridDesignTable;
}

export class RectangularGeogridCalculator {
    /**
     * Gets membrane theory coefficient (Ω) based on design strain percentage
     * Matches Excel MEMBRANE THEORY COEFFICIENT table (rows 51-55)
     * Data source: MembraneCoefficientService (centralized JSON configuration)
     */
    private getMembraneCoefficient(strainPercent: number): number {
        return MembraneCoefficientService.getCoefficient(strainPercent);
    }

    /**
     * Calculate active pressure coefficient Ka
     * Excel formula: (TAN((45-φ/2)*PI()/180))^2
     */
    private calculateActivePressureCoefficient(frictionAngle: number): number {
        const angleRad = ((45 - frictionAngle / 2) * Math.PI) / 180;
        return Math.pow(Math.tan(angleRad), 2);
    }

    /**
     * Calculate VALUES AT LAYERS INTERFACES (Excel B60-B76)
     * Implements exact Excel formulas for rectangular geometry
     */
    calculateInterfaceValues(inputs: RectangularGeogridInputs): RectangularLayerInterfaceValues {
        const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

        // ═══════════════════════════════════════════════════════════════
        // ASPHALT LAYER BOTTOM (Excel B60-B64)
        // ═══════════════════════════════════════════════════════════════
        const z1 = inputs.h1; // B60 = C20 (asphalt thickness)

        // B61: B1 = C8 + (B60 * TAN(RADIANS(C21)))
        const B1 = inputs.B + z1 * Math.tan(toRadians(inputs.α1));

        // B62: L1 = C9 + (B60 * TAN(RADIANS(C21)))
        const L1 = inputs.L + z1 * Math.tan(toRadians(inputs.α1));

        // B63: A1 = 4 * B61 * B62
        const A1 = 4 * B1 * L1;

        // B64: σv1 = C10 * (C11 / B63)
        const σv1 = inputs.q * (inputs.A0 / A1);

        logDebug(
            'RectangularGeogridCalculator',
            'ASPHALT LAYER BOTTOM (Excel B60-B64)',
            {
                z1_B60: z1.toFixed(2) + ' m (= C20 = h1)',
                B1_B61_formula: `${inputs.B.toFixed(2)} + (${z1.toFixed(2)} * tan(${inputs.α1}°))`,
                B1_B61: B1.toFixed(6) + ' m',
                L1_B62_formula: `${inputs.L.toFixed(2)} + (${z1.toFixed(2)} * tan(${inputs.α1}°))`,
                L1_B62: L1.toFixed(6) + ' m',
                A1_B63_formula: `4 * ${B1.toFixed(6)} * ${L1.toFixed(6)}`,
                A1_B63: A1.toFixed(2) + ' m²',
                σv1_B64_formula: `${inputs.q} * (${inputs.A0.toFixed(2)} / ${A1.toFixed(2)})`,
                σv1_B64: σv1.toFixed(2) + ' kPa',
            },
            LogTag.GEOGRID_CALC
        );

        // ═════════════════════════════════════════════════════════════════
        // BASE COURSE BOTTOM (Excel B66-B70) - USA z1, B1, L1, σv1, A1
        // ═════════════════════════════════════════════════════════════════
        // B66: z2 = C20 + C27 (but use z1 + h2 for cascading)
        const z2 = z1 + inputs.h2;

        // B67: B2 = B61 + ((B66 - B60) * TAN(RADIANS(C28)))
        const B2 = B1 + (z2 - z1) * Math.tan(toRadians(inputs.α2));

        // B68: L2 = B62 + ((B66 - B60) * TAN(RADIANS(C28)))
        const L2 = L1 + (z2 - z1) * Math.tan(toRadians(inputs.α2));

        // B69: A2 = 4 * B67 * B68
        const A2 = 4 * B2 * L2;

        // B70: σv2 = B64 * (B63 / B69)
        const σv2 = σv1 * (A1 / A2);

        logDebug(
            'RectangularGeogridCalculator',
            'BASE COURSE BOTTOM (Excel B66-B70)',
            {
                z2_B66: z2.toFixed(2) + ' m (= z1 + h2 = ' + z1.toFixed(2) + ' + ' + inputs.h2.toFixed(2) + ')',
                B2_B67_formula: `${B1.toFixed(6)} + ((${z2.toFixed(2)} - ${z1.toFixed(2)}) * tan(${inputs.α2}°))`,
                B2_B67: B2.toFixed(6) + ' m',
                L2_B68_formula: `${L1.toFixed(6)} + ((${z2.toFixed(2)} - ${z1.toFixed(2)}) * tan(${inputs.α2}°))`,
                L2_B68: L2.toFixed(6) + ' m',
                A2_B69_formula: `4 * ${B2.toFixed(6)} * ${L2.toFixed(6)}`,
                A2_B69: A2.toFixed(2) + ' m²',
                σv2_B70_formula: `${σv1.toFixed(2)} * (${A1.toFixed(2)} / ${A2.toFixed(2)})`,
                σv2_B70: σv2.toFixed(2) + ' kPa',
            },
            LogTag.GEOGRID_CALC
        );

        // ═══════════════════════════════════════════════════════════════════
        // SUBBASE BOTTOM (Excel B72-B76) - USA z2, B2, L2, σv2, A2
        // ═══════════════════════════════════════════════════════════════════
        // B72: z3 = C20 + C27 + C37 (but use z2 + h3 for cascading)
        const z3 = z2 + inputs.h3;

        // B73: B3 = B67 + ((B72 - B66) * TAN(RADIANS(C38)))
        const B3 = B2 + (z3 - z2) * Math.tan(toRadians(inputs.α3));

        // B74: L3 = B68 + ((B72 - B66) * TAN(RADIANS(C38)))
        const L3 = L2 + (z3 - z2) * Math.tan(toRadians(inputs.α3));

        // B75: A3 = 4 * B73 * B74
        const A3 = 4 * B3 * L3;

        // B76: σv3 = B70 * (B69 / B75)
        const σv3 = σv2 * (A2 / A3);

        logDebug(
            'RectangularGeogridCalculator',
            'SUBBASE BOTTOM (Excel B72-B76)',
            {
                z3_B72: z3.toFixed(2) + ' m (= z2 + h3 = ' + z2.toFixed(2) + ' + ' + inputs.h3.toFixed(2) + ')',
                B3_B73_formula: `${B2.toFixed(6)} + ((${z3.toFixed(2)} - ${z2.toFixed(2)}) * tan(${inputs.α3}°))`,
                B3_B73: B3.toFixed(6) + ' m',
                L3_B74_formula: `${L2.toFixed(6)} + ((${z3.toFixed(2)} - ${z2.toFixed(2)}) * tan(${inputs.α3}°))`,
                L3_B74: L3.toFixed(6) + ' m',
                A3_B75_formula: `4 * ${B3.toFixed(6)} * ${L3.toFixed(6)}`,
                A3_B75: A3.toFixed(2) + ' m²',
                σv3_B76_formula: `${σv2.toFixed(2)} * (${A2.toFixed(2)} / ${A3.toFixed(2)})`,
                σv3_B76: σv3.toFixed(2) + ' kPa',
            },
            LogTag.GEOGRID_CALC
        );

        return {
            asphaltLayerBottom: { z1, B1, L1, A1, σv1 },
            baseCourseBottom: { z2, B2, L2, A2, σv2 },
            subbaseBottom: { z3, B3, L3, A3, σv3 },
        };
    }

    /**
     * Calculate BASE COURSE geogrid design (Excel B80-B100)
     * Implements exact Excel formulas for all 6 layers
     */
    calculateBaseCourse(inputs: RectangularGeogridInputs, interfaceValues: RectangularLayerInterfaceValues): RectangularLayerCalculation[] {
        const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
        const layers: RectangularLayerCalculation[] = [];
        const H29 = inputs.Nr2 + 1; // Excel H29 = C29+1

        // Pre-calculate values
        const { asphaltLayerBottom, baseCourseBottom } = interfaceValues;

        for (let layerNum = 1; layerNum <= 6; layerNum++) {
            // H (m) - from base course bottom (Excel B81-G81)
            const H = inputs.H_base[layerNum - 1] || 0; // Use exact Excel H values from input array

            // Z (m) - from top (Excel B82-G82)
            const Z = layerNum >= H29 ? asphaltLayerBottom.z1 : baseCourseBottom.z2 - H; // IF(B80>=$H$29,$B$60,$B$66-B81)

            // Next layer Z for calculations - Excel formula C82: =IF(C80>=$H$29,$B$60,$B$66-C81)
            let Z_next = asphaltLayerBottom.z1; // Default for layers >= H29
            if (layerNum + 1 <= 6 && layerNum + 1 < H29) {
                const H_next = inputs.H_base[layerNum] || 0; // H of next layer (C81, D81, etc.)
                Z_next = baseCourseBottom.z2 - H_next; // $B$66-C81 formula
            }

            // Tz (kN/m) - Earth pressure component (Excel B83-G83)
            // Excel B83: IF(B80>=$H$29,0,(0.5*$C$31*(2*$C$19*$B$60+$C$24*(B82+C82-2*$B$60))*(B82-C82)))
            // Only the Container Yard B83 (layer 1) is wrapped in IF($C$37=0,0,...); its C83-G83 and every Railway cell are not
            let Tz = 0;
            if (inputs.workbook === 'containerYard' && layerNum === 1 && inputs.h3 === 0) {
                Tz = 0;
            } else if (layerNum < H29) {
                Tz = 0.5 * inputs.K2 * (2 * inputs.γ1 * asphaltLayerBottom.z1 + inputs.γ2 * (Z + Z_next - 2 * asphaltLayerBottom.z1)) * (Z - Z_next);
            }

            // A (m²) - Area at this layer (Excel B84-G84)
            const A =
                layerNum >= H29
                    ? asphaltLayerBottom.A1
                    : 4 *
                      (asphaltLayerBottom.B1 + (Z - asphaltLayerBottom.z1) * Math.tan(toRadians(inputs.α2))) *
                      (asphaltLayerBottom.L1 + (Z - asphaltLayerBottom.z1) * Math.tan(toRadians(inputs.α2)));

            // σv (kPa) - Vertical stress (Excel B85-G85)
            const σv = layerNum > H29 ? 0 : (asphaltLayerBottom.σv1 * asphaltLayerBottom.A1) / A;

            // Tp (kN/m) - Geogrid tension component (Excel B86-G86)
            let Tp = 0;
            if (layerNum <= H29) {
                const A_next =
                    layerNum + 1 >= H29
                        ? asphaltLayerBottom.A1
                        : 4 *
                          (asphaltLayerBottom.B1 + (Z_next - asphaltLayerBottom.z1) * Math.tan(toRadians(inputs.α2))) *
                          (asphaltLayerBottom.L1 + (Z_next - asphaltLayerBottom.z1) * Math.tan(toRadians(inputs.α2)));
                const σv_next = (asphaltLayerBottom.σv1 * asphaltLayerBottom.A1) / A_next;
                Tp = 0.5 * inputs.K2 * (σv + σv_next) * (Z - Z_next);
            }

            // Design strain and membrane coefficient (Excel B87-B88, B99-G99)
            const εr = inputs.designStrainBase[layerNum - 1] || 5; // Use exact Excel strain values from input array
            const Ω = this.getMembraneCoefficient(εr);

            // First lift calculations (only for layer 1) (Excel B89-B95)
            let firstLift: RectangularLayerCalculation['firstLift'];
            let Tm2 = 0;

            if (layerNum === 1) {
                const r0 = inputs.r0; // B89 = C14
                const h_star = inputs.h_star; // B90 = C16
                const rf = r0 + h_star * Math.tan(toRadians(inputs.α2)); // B91
                const pw = inputs.pw; // B92 = C15
                const Af = (Math.PI / 4) * Math.pow(rf, 2); // B93 = (3.14/4)*(B91^2)

                // B94/B95 condition: Container Yard IF($C$27=0,0,...) (no base course), Railway IF($C$37>0,0,...) (a subballast)
                const hasFirstLiftMembrane = inputs.workbook === 'containerYard' ? inputs.h2 !== 0 : !(inputs.h3 > 0);

                // Wtc2 (kPa) calculation (Excel B94)
                const term1 = ((1 / 3) * inputs.γ2 * (Math.pow(rf, 3) - Math.pow(r0, 3))) / (Math.pow(rf, 2) * Math.tan(toRadians(inputs.α2)));
                const term2 = pw * (Math.pow(r0, 2) / Math.pow(rf, 2));
                const term3 = 60 * Math.PI * (inputs.CBR / (inputs.FS || 3));
                const Wtc2 = hasFirstLiftMembrane ? term1 + term2 - term3 : 0;

                // Tm2 (kN/m) calculation (Excel B95): IF(<condition>,0,IF(B94<=0,0,B94*B91*B88))
                Tm2 = Wtc2 <= 0 ? 0 : Wtc2 * rf * Ω;

                firstLift = { r0, h_star, rf, pw, Af, Wtc: Wtc2, Tm: Tm2 };
            }

            // Ttot (kN/m) - Total tension (Excel B96-G96)
            const Ttot = Tz + Tp + (layerNum === 1 ? Tm2 : 0);

            layers.push({
                layerNumber: layerNum,
                H,
                Z,
                Tz,
                A,
                σv,
                Tp,
                εr,
                Ω,
                Ttot,
                firstLift,
            });

            logDebug(
                'RectangularGeogridCalculator',
                `Base course layer ${layerNum} calculated`,
                {
                    H: H.toFixed(4),
                    Z: Z.toFixed(4),
                    Tz: Tz.toFixed(6),
                    A: A.toFixed(2),
                    σv: σv.toFixed(2),
                    Tp: Tp.toFixed(6),
                    Ttot: Ttot.toFixed(6),
                    firstLift: firstLift
                        ? {
                              Wtc: firstLift.Wtc.toFixed(6),
                              Tm: firstLift.Tm.toFixed(6),
                          }
                        : undefined,
                },
                LogTag.GEOGRID_CALC
            );
        }

        return layers;
    }

    /**
     * Calculate SUBBASE COURSE geogrid design (Excel B104-B124)
     * Implements exact Excel formulas for all 6 layers
     */
    calculateSubbaseCourse(inputs: RectangularGeogridInputs, interfaceValues: RectangularLayerInterfaceValues): RectangularLayerCalculation[] {
        const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
        const layers: RectangularLayerCalculation[] = [];
        const H39 = inputs.Nr3 + 1; // Excel H39 = C39+1

        // Pre-calculate values
        const { asphaltLayerBottom, baseCourseBottom, subbaseBottom } = interfaceValues;

        for (let layerNum = 1; layerNum <= 6; layerNum++) {
            // Excel guard: IF($C$37=0,0,...) - when h3=0, return 0 for all subbase calculations
            if (inputs.h3 === 0) {
                layers.push({
                    layerNumber: layerNum,
                    H: 0,
                    Z: 0,
                    Tz: 0,
                    A: 0,
                    σv: 0,
                    Tp: 0,
                    εr: 0,
                    Ω: 0,
                    Ttot: 0,
                });
                continue;
            }

            // H (m) - from base course bottom (Excel B105-G105)
            const H = inputs.H_subbase[layerNum - 1] || 0; // Use exact Excel H values from input array

            // Z (m) - from top (Excel B106-G106)
            const Z = layerNum >= H39 ? baseCourseBottom.z2 : subbaseBottom.z3 - H;

            // Next layer Z for calculations - Excel formula C106: =IF($C$37=0,0,IF(C104>=$H$39,$B$66,$B$72-C105))
            let Z_next = baseCourseBottom.z2; // Default for layers >= H39
            if (layerNum + 1 <= 6 && layerNum + 1 < H39) {
                const H_next = inputs.H_subbase[layerNum] || 0; // H of next layer (C105, D105, etc.)
                Z_next = subbaseBottom.z3 - H_next; // $B$72-C105 formula
            }

            // Tz (kN/m) - Earth pressure component for SUBBASE (Excel B107-G107)
            // Excel: 0.5*$C$41*(2*($C$19*$B$60+$C$24*$B$66-$C$24*$B$60)+$C$34*(B106+C106-2*$B$66))*(B106-C106)
            let Tz = 0;
            if (layerNum < H39) {
                const term1 = 2 * (inputs.γ1 * asphaltLayerBottom.z1 + inputs.γ2 * baseCourseBottom.z2 - inputs.γ2 * asphaltLayerBottom.z1);
                const term2 = inputs.γ3 * (Z + Z_next - 2 * baseCourseBottom.z2);
                Tz = 0.5 * inputs.K3 * (term1 + term2) * (Z - Z_next);
            }

            // A (m²) - Area at this layer (Excel B108-G108)
            const A =
                layerNum >= H39
                    ? baseCourseBottom.A2
                    : 4 * (baseCourseBottom.B2 + (Z - baseCourseBottom.z2) * Math.tan(toRadians(inputs.α3))) * (baseCourseBottom.L2 + (Z - baseCourseBottom.z2) * Math.tan(toRadians(inputs.α3)));

            // σv (kPa) - Vertical stress (Excel B109-G109)
            const σv = layerNum > H39 ? 0 : (baseCourseBottom.σv2 * baseCourseBottom.A2) / A;

            // Tp (kN/m) - Geogrid tension component for SUBBASE (Excel B110-G110)
            let Tp = 0;
            if (layerNum <= H39) {
                const A_next =
                    layerNum + 1 >= H39
                        ? baseCourseBottom.A2
                        : 4 *
                          (baseCourseBottom.B2 + (Z_next - baseCourseBottom.z2) * Math.tan(toRadians(inputs.α3))) *
                          (baseCourseBottom.L2 + (Z_next - baseCourseBottom.z2) * Math.tan(toRadians(inputs.α3)));
                const σv_next = (baseCourseBottom.σv2 * baseCourseBottom.A2) / A_next;
                Tp = 0.5 * inputs.K3 * (σv + σv_next) * (Z - Z_next);
            }

            // Design strain and membrane coefficient (Excel B111-B112, B123-G123)
            const εr = inputs.designStrainSubbase[layerNum - 1] || 5; // Use exact Excel strain values from input array
            const Ω = this.getMembraneCoefficient(εr);

            // First lift calculations for SUBBASE (only for layer 1) (Excel B113-B119)
            let firstLift: RectangularLayerCalculation['firstLift'];
            let Tm3 = 0;

            if (layerNum === 1) {
                const r0 = inputs.r0; // B113 = C14
                const h_star = inputs.h_star; // B114 = C16
                const rf = r0 + h_star * Math.tan(toRadians(inputs.α3)); // B115
                const pw = inputs.pw; // B116 = C15
                const Af = (Math.PI / 4) * Math.pow(rf, 2); // B117 = (3.14/4)*(B115^2)

                // Wtc3 (kPa) calculation (Excel B118)
                const term1 = ((1 / 3) * inputs.γ3 * (Math.pow(rf, 3) - Math.pow(r0, 3))) / (Math.pow(rf, 2) * Math.tan(toRadians(inputs.α3)));
                const term2 = pw * (Math.pow(r0, 2) / Math.pow(rf, 2));
                const term3 = 60 * Math.PI * (inputs.CBR / (inputs.FS || 3));
                const Wtc3 = term1 + term2 - term3;

                // Tm3 (kN/m) calculation (Excel B119)
                Tm3 = Wtc3 <= 0 ? 0 : Wtc3 * rf * Ω;

                firstLift = { r0, h_star, rf, pw, Af, Wtc: Wtc3, Tm: Tm3 };
            }

            // Ttot (kN/m) - Total tension for SUBBASE (Excel B120-G120)
            const Ttot = Tz + Tp + (layerNum === 1 ? Tm3 : 0);

            layers.push({
                layerNumber: layerNum,
                H,
                Z,
                Tz,
                A,
                σv,
                Tp,
                εr,
                Ω,
                Ttot,
                firstLift,
            });

            logDebug(
                'RectangularGeogridCalculator',
                `Subbase course layer ${layerNum} calculated`,
                {
                    H: H.toFixed(4),
                    Z: Z.toFixed(4),
                    Tz: Tz.toFixed(6),
                    A: A.toFixed(2),
                    σv: σv.toFixed(2),
                    Tp: Tp.toFixed(6),
                    Ttot: Ttot.toFixed(6),
                    firstLift: firstLift
                        ? {
                              Wtc: firstLift.Wtc.toFixed(6),
                              Tm: firstLift.Tm.toFixed(6),
                          }
                        : undefined,
                },
                LogTag.GEOGRID_CALC
            );
        }

        return layers;
    }

    /**
     * Calculate complete rectangular geogrid design table
     * Returns all interface values, base course, and subbase course calculations
     */
    calculateRectangularGeogridDesign(inputs: RectangularGeogridInputs): RectangularGeogridDesignTable {
        if (inputs.workbook !== 'containerYard' && inputs.workbook !== 'railway') {
            throw new Error(`Unknown rectangular GeoGrid workbook: ${String(inputs.workbook)}`);
        }
        logDebug(
            'RectangularGeogridCalculator',
            'Starting rectangular geogrid design calculation',
            {
                geometry: 'Rectangular',
                B: inputs.B,
                L: inputs.L,
                q: inputs.q,
                h2: inputs.h2,
                h3: inputs.h3,
                Nr2: inputs.Nr2,
                Nr3: inputs.Nr3,
            },
            LogTag.GEOGRID_CALC
        );

        // Step 1: Calculate interface values
        const interfaceValues = this.calculateInterfaceValues(inputs);

        // Step 2: Calculate base course
        const baseCourse = this.calculateBaseCourse(inputs, interfaceValues);

        // Step 3: Calculate subbase course
        const subbaseCourse = this.calculateSubbaseCourse(inputs, interfaceValues);

        logDebug(
            'RectangularGeogridCalculator',
            'Rectangular geogrid design calculation completed',
            {
                baseCourseLayers: baseCourse.length,
                subbaseCourseLayers: subbaseCourse.length,
                baseCourseLayer1_Ttot: baseCourse[0]?.Ttot.toFixed(6),
                subbaseCourseLayer1_Ttot: subbaseCourse[0]?.Ttot.toFixed(6),
            },
            LogTag.GEOGRID_CALC
        );

        return {
            interfaceValues,
            baseCourse,
            subbaseCourse,
        };
    }
}

/**
 * Maps IGeogridData to RectangularGeogridInputs for rectangular geometry calculations
 * @param geoGridData - The geogrid data from project
 * @param geogridIndex - The index of the specific geogrid (0-based) - currently unused but kept for API consistency
 * @param courseType - 'base' or 'subbase' to specify which course the geogrid belongs to
 * @returns Mapped rectangular inputs or null if invalid data
 */
export function mapRectangularGeogridDataToCalculationInputs(geoGridData: any /* IGeogridData */, geogridIndex = 0, courseType: 'base' | 'subbase' = 'base'): RectangularGeogridInputs | null {
    // Accept both Rectangular and Railway geometry types
    // Railway uses rectangular calculations with surface layer h1=0
    const geometryType = geoGridData?.type?.geogridType;
    if (!geoGridData || (geometryType !== 'Rectangular' && geometryType !== 'Railway')) {
        logDebug('mapRectangularGeogridData', 'Invalid geometry type for rectangular calculations', { geogridType: geometryType }, LogTag.GEOGRID_CALC);
        return null;
    }

    try {
        logDebug('mapRectangularGeogridData', 'Starting rectangular data mapping', { courseType, geogridIndex }, LogTag.GEOGRID_CALC);

        // LOAD SECTION - Use deckHalfWidth/Length for rectangular
        const B = geoGridData.load?.deckHalfWidth ?? 2.0; // m
        const L = geoGridData.load?.deckHalfLength ?? 5.0; // m
        const q = geoGridData.load?.uniformLoad ?? 80; // kPa
        const A0 = B * L * 4; // Contact area (m²) - Excel C11

        // TRUCK LOAD FOR FIRST LIFT SECTION
        const r0 = geoGridData.load?.wheelLoadRadius ?? geoGridData.truck?.wheelRadius ?? 0.15; // m
        const pw = geoGridData.load?.tireInflationPressure ?? geoGridData.truck?.tirePressure ?? 500; // kPa
        const h_star = (geoGridData.load as any)?.thickness ?? geoGridData.truck?.thickness ?? 0.3; // m

        // TOP COURSE SECTION (asphalt layer)
        const γ1 = geoGridData.surfaceLayer?.unitWeight ?? 19; // kN/m³
        const h1 = geoGridData.surfaceLayer?.thickness ?? 0; // m (typically 0 for geogrid)
        const α1 = 55; // degrees - load spreading angle

        // BASE COURSE SECTION
        const baseCourseProps = geoGridData.baseCourse ?? geoGridData.geogridBaseCourse;
        const γ2 = baseCourseProps?.unitWeight ?? 19; // kN/m³
        const φ2 = baseCourseProps?.frictionAngle ?? 30; // degrees
        const c2 = baseCourseProps?.cohesion ?? 0; // kPa
        const h2 = geoGridData.geogridBaseCourse?.thickness ?? 0.4; // m
        const α2 = baseCourseProps?.loadSpreadingAngle ?? 50; // degrees
        const baseCourseData = geoGridData.geogridBaseCourse;
        const Nr2 = Math.max(1, baseCourseData?.geogrids?.length ?? 1);
        const Sv2 = Nr2 > 0 ? h2 / Nr2 : 0; // Average spacing
        const K2 = Math.pow(Math.tan(((45 - φ2 / 2) * Math.PI) / 180), 2); // Ka

        // SUBBASE COURSE SECTION
        const subbaseCourseProps = geoGridData.subBaseCourse ?? geoGridData.geogridSubBaseCourse;
        const γ3 = subbaseCourseProps?.unitWeight ?? 19; // kN/m³
        const φ3 = subbaseCourseProps?.frictionAngle ?? 30; // degrees
        const c3 = subbaseCourseProps?.cohesion ?? 0; // kPa
        const h3 = geoGridData.geogridSubBaseCourse?.thickness ?? 0.4; // m
        const α3 = subbaseCourseProps?.loadSpreadingAngle ?? 50; // degrees
        const subbaseCourseData = geoGridData.geogridSubBaseCourse;
        const Nr3 = Math.max(1, subbaseCourseData?.geogrids?.length ?? 1);
        const Sv3 = Nr3 > 0 ? h3 / Nr3 : 0; // Average spacing
        const K3 = Math.pow(Math.tan(((45 - φ3 / 2) * Math.PI) / 180), 2); // Ka

        // SUBGRADE SECTION
        const CBR = geoGridData.subgrade?.cbrSubgrade ?? 0.5;
        const FS = resolveGeogridSafetyFactor(geoGridData.subgrade?.safetyFactor); // Excel C45

        // LAYER SPECIFIC ARRAYS - Map from actual geogrid data
        // H values represent geogrid height from base course bottom (Excel B81-G81, B105-G105)
        // Extract position values from geogrids array - user can set these to 0 or any value
        const H_base = [0, 0, 0, 0, 0, 0]; // Initialize with zeros
        const H_subbase = [0, 0, 0, 0, 0, 0]; // Initialize with zeros

        // Initialize design strain arrays with defaults
        const designStrainBase = [5, 2, 3, 4, 5, 2]; // Default Excel B99-G99 (%)
        const designStrainSubbase = [5, 1, 3, 4, 5, 2]; // Default Excel B123-G123 (%)

        // Map base course geogrid positions and design strains to arrays
        if (baseCourseData?.geogrids && Array.isArray(baseCourseData.geogrids)) {
            baseCourseData.geogrids.forEach((geogrid: any, index: number) => {
                if (index < 6) {
                    // Use geogrid.position if available, otherwise default to 0
                    H_base[index] = geogrid?.position ?? 0;
                    // Use actual design strain from geogrid data (ensure it's a number)
                    designStrainBase[index] = Number(geogrid?.designStrain) || 5;
                }
            });
        }

        // Map subbase course geogrid positions and design strains to arrays
        if (subbaseCourseData?.geogrids && Array.isArray(subbaseCourseData.geogrids)) {
            subbaseCourseData.geogrids.forEach((geogrid: any, index: number) => {
                if (index < 6) {
                    // Use geogrid.position if available, otherwise default to 0
                    H_subbase[index] = geogrid?.position ?? 0;
                    // Use actual design strain from geogrid data (ensure it's a number)
                    designStrainSubbase[index] = Number(geogrid?.designStrain) || 5;
                }
            });
        }

        const inputs: RectangularGeogridInputs = {
            workbook: geometryType === 'Railway' ? 'railway' : 'containerYard',
            B,
            L,
            q,
            A0,
            r0,
            pw,
            h_star,
            γ1,
            h1,
            α1,
            γ2,
            φ2,
            c2,
            h2,
            α2,
            Nr2,
            Sv2,
            K2,
            γ3,
            φ3,
            c3,
            h3,
            α3,
            Nr3,
            Sv3,
            K3,
            CBR,
            FS,
            H_base,
            H_subbase,
            designStrainBase,
            designStrainSubbase,
        };

        logDebug(
            'mapRectangularGeogridData',
            'Rectangular data mapping complete',
            {
                B: B.toFixed(2),
                L: L.toFixed(2),
                q: q.toFixed(1),
                h2: h2.toFixed(2),
                h3: h3.toFixed(2),
                Nr2,
                Nr3,
                H_base: H_base.map(h => h.toFixed(3)).join(', '),
                H_subbase: H_subbase.map(h => h.toFixed(3)).join(', '),
            },
            LogTag.GEOGRID_CALC
        );

        return inputs;
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logDebug('mapRectangularGeogridData', 'Error mapping rectangular data', { error: errorMessage }, LogTag.GEOGRID_CALC);
        return null;
    }
}

/**
 * Calculates the required tensile strength (Ttot) for a specific geogrid in rectangular geometry
 * This is the rectangular equivalent of calculateGeogridRequiredStrength from GeogridTtotService
 * @param geoGridData - The geogrid data from project
 * @param geogridIndex - The index of the specific geogrid (0-based)
 * @param courseType - 'base' or 'subbase' to specify which course the geogrid belongs to
 * @returns The calculated Ttot value in kN/m, or null if calculation fails
 */
export function calculateRectangularGeogridRequiredStrength(geoGridData: any /* IGeogridData */, geogridIndex = 0, courseType: 'base' | 'subbase' = 'base'): number | null {
    logDebug(
        'calculateRectangularGeogridRequiredStrength',
        `Called with courseType: ${courseType}, geogridIndex: ${geogridIndex}`,
        { geometryType: geoGridData?.type?.geogridType },
        LogTag.GEOGRID_CALC
    );

    // Map the geogrid data to rectangular inputs
    const inputs = mapRectangularGeogridDataToCalculationInputs(geoGridData, geogridIndex, courseType);

    if (!inputs) {
        logDebug('calculateRectangularGeogridRequiredStrength', `No rectangular inputs returned for courseType: ${courseType}`, undefined, LogTag.GEOGRID_CALC);
        return null;
    }

    // Calculate Ttot for the specific geogrid layer
    const result = calculateRectangularGeogridTtot(inputs, geogridIndex, courseType);

    logDebug(
        'calculateRectangularGeogridRequiredStrength',
        `Result for courseType ${courseType}`,
        {
            status: result.status,
            value: result.status === 'success' ? result.value : null,
            geogridIndex,
        },
        LogTag.GEOGRID_CALC
    );

    if (result.status === 'success') {
        return result.value;
    } else {
        return null;
    }
}

/**
 * Main function to calculate rectangular geogrid Ttot
 * @param inputs - Rectangular geometry input parameters with required arrays:
 *   - H_base: [0.1, 0, 0, 0, 0, 0] (Excel B81-G81)
 *   - H_subbase: [0.1, 0.4, 0, 0, 0, 0] (Excel B105-G105)
 *   - designStrainBase: [5, 2, 3, 4, 5, 2] (Excel B99-G99)
 *   - designStrainSubbase: [5, 1, 3, 4, 5, 2] (Excel B123-G123)
 * @param targetLayerIndex - Target layer index (0-based)
 * @param courseType - 'base' or 'subbase'
 * @returns GeogridResult with Ttot value and debug table
 */
export function calculateRectangularGeogridTtot(inputs: RectangularGeogridInputs, targetLayerIndex = 0, courseType: 'base' | 'subbase' = 'subbase'): RectangularGeogridResult {
    try {
        const calculator = new RectangularGeogridCalculator();
        const designTable = calculator.calculateRectangularGeogridDesign(inputs);

        let targetValue: number;
        let columnName: string;

        if (courseType === 'base') {
            targetValue = designTable.baseCourse[targetLayerIndex]?.Ttot || 0;
            columnName = `B${96 + targetLayerIndex}`; // Excel B96, C96, etc.
        } else {
            targetValue = designTable.subbaseCourse[targetLayerIndex]?.Ttot || 0;
            columnName = `B${120 + targetLayerIndex}`; // Excel B120, C120, etc.
        }

        logDebug(
            'calculateRectangularGeogridTtot',
            `Rectangular calculation completed - ${courseType} course`,
            {
                targetLayerIndex,
                courseType,
                targetValue: targetValue.toFixed(6),
                columnName,
            },
            LogTag.GEOGRID_CALC
        );

        return {
            value: targetValue,
            column: columnName,
            status: 'success',
            message: `Rectangular ${courseType} course layer ${targetLayerIndex + 1} Ttot calculated successfully`,
            designTable,
        };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logDebug('calculateRectangularGeogridTtot', 'Error in rectangular calculation', { error: errorMessage }, LogTag.GEOGRID_CALC);

        return {
            value: 0,
            column: 'ERROR',
            status: 'error',
            message: `Error in rectangular calculation: ${errorMessage}`,
        };
    }
}

/**
 * Interface for tensile strength breakdown values (Tz, Tp, Tm)
 * Used for displaying the 3 contributions in online results and PDF reports
 */
export interface TensileBreakdown {
    Tz: number; // Earth pressure component (kN/m)
    Tp: number; // Geogrid tension component (kN/m)
    Tm: number; // Membrane effect - first lift only (kN/m)
    Ttot: number; // Total = Tz + Tp + Tm (kN/m)
}

/**
 * Calculates the tensile strength breakdown (Tz, Tp, Tm) for a specific geogrid layer.
 * Used for displaying the 3 contributions in online results and PDF reports.
 * This is the RECTANGULAR geometry version.
 * @param geoGridData - The geogrid data from project
 * @param geogridIndex - The index of the specific geogrid (0-based)
 * @param courseType - 'base' or 'subbase' to specify which course the geogrid belongs to
 * @returns TensileBreakdown object with Tz, Tp, Tm, Ttot values, or null if calculation fails
 */
export function calculateRectangularGeogridStrengthBreakdown(geoGridData: any /* IGeogridData */, geogridIndex = 0, courseType: 'base' | 'subbase' = 'base'): TensileBreakdown | null {
    logDebug(
        'calculateRectangularGeogridStrengthBreakdown',
        `Called with courseType: ${courseType}, geogridIndex: ${geogridIndex}`,
        { geometryType: geoGridData?.type?.geogridType },
        LogTag.GEOGRID_CALC
    );

    // Map the geogrid data to rectangular inputs
    const inputs = mapRectangularGeogridDataToCalculationInputs(geoGridData, geogridIndex, courseType);

    if (!inputs) {
        logDebug('calculateRectangularGeogridStrengthBreakdown', `No rectangular inputs returned for courseType: ${courseType}`, undefined, LogTag.GEOGRID_CALC);
        return null;
    }

    try {
        const calculator = new RectangularGeogridCalculator();
        const designTable = calculator.calculateRectangularGeogridDesign(inputs);

        // Select the appropriate course data
        const courseData = courseType === 'base' ? designTable.baseCourse : designTable.subbaseCourse;

        // Get the layer at the specified index
        const layerData = courseData[geogridIndex];

        if (!layerData) {
            return null;
        }

        // Extract Tz, Tp from layer data
        const Tz = layerData.Tz ?? 0;
        const Tp = layerData.Tp ?? 0;

        // Tm is only applied to layer 1 (index 0)
        let Tm = 0;
        if (geogridIndex === 0 && layerData.firstLift) {
            Tm = layerData.firstLift.Tm ?? 0;
        }

        const Ttot = Tz + Tp + Tm;

        logDebug(
            'calculateRectangularGeogridStrengthBreakdown',
            `Breakdown result for ${courseType}`,
            { Tz: Tz.toFixed(4), Tp: Tp.toFixed(4), Tm: Tm.toFixed(4), Ttot: Ttot.toFixed(4) },
            LogTag.GEOGRID_CALC
        );

        return {
            Tz: parseFloat(Tz.toFixed(4)),
            Tp: parseFloat(Tp.toFixed(4)),
            Tm: parseFloat(Tm.toFixed(4)),
            Ttot: parseFloat(Ttot.toFixed(4)),
        };
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logDebug('calculateRectangularGeogridStrengthBreakdown', 'Error calculating breakdown', { error: errorMessage }, LogTag.GEOGRID_CALC);
        return null;
    }
}
